import React from 'react';
import { Button, Paper } from '@mui/material';

import { BasicProfile, GableSettings } from '@paragon/contract-library';
import { PropertiesPanel } from '@paragon/layout-models';
import { DistanceField } from '@paragon/material';

import { rewebTruss } from '../../../../store/actions/editComponentActions';
import { setRewebbingProfileSettingsWithUpdatedJoints } from '../../../../store/actions/profileActions';
import { selectItem } from '../../../../store/actions/selectActions';
import { getActiveComponentDesign } from '../../../../store/selectors/getActiveComponentDesignSelector';
import { getSingleSelectedItemEntryOrNull } from '../../../../store/selectors/getSelectedItemSelector';
import { clearRewebbingInfo, RewebbingProfileSettings } from '../../../../store/slices/applicationSlice';
import { setTrussEditsPanel } from '../../../../store/slices/panelDataSlice';
import { useAppDispatch, useAppSelector } from '../../../hooks';
import WebJointEditor from '../../subpanels/insertions/component/profile-creator/WebJointEditor';
import WebJointSegmentEditor from '../../subpanels/insertions/component/profile-creator/WebJointSegmentEditor';
import { GableSettingsSubPanel } from '../../subpanels/properties/GableSettings';
import { LabeledBox } from '../../subpanels/properties/LabeledBox';

export function RewebProfileTrussPanel() {
  const dispatch = useAppDispatch();
  const rewebbingInfo = useAppSelector((state) => state.application.rewebbingInfo);
  const activeComponentDesign = useAppSelector(getActiveComponentDesign);
  const selectedItemType = useAppSelector(getSingleSelectedItemEntryOrNull)?.type;

  if (!rewebbingInfo) return null;

  const { profileSettings } = rewebbingInfo;

  const cancelReweb = () => {
    dispatch(clearRewebbingInfo());
    dispatch(setTrussEditsPanel(null));
    dispatch(selectItem(activeComponentDesign, 'componentDesign'));
  };

  const setPanelLength = (chord: 'top' | 'bottom') => async (value: number) => {
    const panelLengthProperty: keyof BasicProfile = `${chord}ChordMaxPanelLength` as const;
    const newProfileSettings: RewebbingProfileSettings = {
      ...profileSettings,
      profile: { ...profileSettings.profile, [panelLengthProperty]: value },
    };
    dispatch(setRewebbingProfileSettingsWithUpdatedJoints(newProfileSettings));
    await dispatch(rewebTruss());
  };

  const updateGableSettings = async (gableSettings: GableSettings) => {
    const updatedProfileSettings: RewebbingProfileSettings = { ...profileSettings, gableSettings };
    dispatch(setRewebbingProfileSettingsWithUpdatedJoints(updatedProfileSettings));
    await dispatch(rewebTruss());
  };

  const editorTool =
    selectedItemType === 'webJointSegment' ? (
      <LabeledBox title="Web Joint Segment Settings" contentSx={{ padding: 1, paddingTop: 2 }}>
        <WebJointSegmentEditor />
      </LabeledBox>
    ) : selectedItemType === 'webJoint' ? (
      <LabeledBox title="Web Joint Settings" contentSx={{ padding: 1, paddingTop: 2 }}>
        <WebJointEditor />
      </LabeledBox>
    ) : null;

  return (
    <PropertiesPanel title="Reweb Truss">
      <LabeledBox title="Max Panel Lengths" contentSx={{ paddingX: 1, display: 'flex', gap: 1 }}>
        <DistanceField
          label="Top"
          value={profileSettings.profile.topChordMaxPanelLength ?? 0}
          onSubmit={setPanelLength('top')}
          min={0}
        />
        <DistanceField
          label="Bottom"
          value={profileSettings.profile.bottomChordMaxPanelLength ?? 0}
          onSubmit={setPanelLength('bottom')}
          min={0}
        />
      </LabeledBox>

      {editorTool}

      <LabeledBox title="Make Gable" contentSx={{ padding: 1 }}>
        <GableSettingsSubPanel value={profileSettings.gableSettings} onChange={updateGableSettings} />
      </LabeledBox>

      <Paper sx={{ display: 'flex', padding: 1, borderRadius: 0 }}>
        <Button variant="contained" sx={{ ml: 'auto' }} onClick={cancelReweb} aria-label="Close">
          Close
        </Button>
      </Paper>
    </PropertiesPanel>
  );
}
